import React, { useState } from 'react';
import Popover from './Popover';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTag, faCalendar, faPersonChalkboard,
    faBookOpenReader, faChair, faBookOpen, faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';

import {
    getDaysOfWeek,
    formatTime,
    formatDateRange,
    createMarkup, formatDateRangeDuration
} from '../utils/formatters';

function CRNColumns({ crn, sections, onlinePop, setDrawerVisible, setSelectedCRN, setInstructorDrawerOpen, setInstructorData, renderTableBody = true, groupDescription, sortBy, sortOrder }) {
    // Group sections by CRN and create separate rows for different meeting details
    if (!sections || sections.length === 0) {
        return null;
    }

    const item = sections[0];
    const instructorName = item.pfaName;
    const instructorEmail = item.pfaEmail;
    const secondaryInsturctorNames = item?.sfaName?.split(',');
    const secondaryInsturctorEmails = item?.sfaEmail?.split(',');

    // Status badge configuration
    const getStatusConfig = (status) => {
        const statusMap = {
            'C': { label: 'Closed', className: 'bg-red-100 text-red-700' },
            'O': { label: 'Open', className: 'bg-green-100 text-green-700' },
            'W': { label: 'Waitlist', className: 'bg-blue-100 text-blue-700' },
            'R': { label: 'Reserved', className: 'bg-yellow-100 text-yellow-800' }
        };
        return statusMap[status] || { label: 'Cancel', className: 'bg-gray-100 text-gray-600' };
    };

    const groupedSections = sections.reduce((acc, sec) => {
        if (!acc[sec.crn]) {
            acc[sec.crn] = [];
        }

        // Add all sections - let the data source handle deduplication
        acc[sec.crn].push({
            ...sec,
            isFirstRowForCRN: acc[sec.crn].length === 0
        });

        return acc;
    }, {});

    const uniqueSections = [];
    Object.entries(groupedSections).forEach(([crn, crnSections]) => {
        crnSections.forEach((sec, index) => {
            uniqueSections.push({
                ...sec,
                isFirstRowForCRN: index === 0,
                rowSpanForCRN: crnSections.length
            });
        });
    });


    // Flatten the grouped sections while maintaining CRN grouping info

    // Use custom sorting if provided, otherwise use default sorting
    const sortedSections = [...uniqueSections].sort((a, b) => {
        // Always sort by CRN first to keep sections with same CRN together
        const crnComparison = a.crn.localeCompare(b.crn);
        if (crnComparison !== 0) {
            return crnComparison;
        }

        if (sortBy && sortOrder) {
            let compareValue = 0;

            switch (sortBy) {
                case 'status':
                    const statusA = a.fwSectOpenInd || '';
                    const statusB = b.fwSectOpenInd || '';
                    compareValue = statusA.localeCompare(statusB);
                    break;
                case 'crn':
                    // Already handled above
                    compareValue = 0;
                    break;
                case 'unit':
                    compareValue = (a.creditHrLow || 0) - (b.creditHrLow || 0);
                    break;
                case 'type':
                    const typeA = a.schdCode || '';
                    const typeB = b.schdCode || '';
                    compareValue = typeA.localeCompare(typeB);
                    break;
                case 'meet':
                    const meetA = a.ssrmeetSchdCode || '';
                    const meetB = b.ssrmeetSchdCode || '';
                    compareValue = meetA.localeCompare(meetB);
                    break;
                case 'time':
                    const timeA = a.beginTime || 'ZZZ';
                    const timeB = b.beginTime || 'ZZZ';
                    compareValue = timeA.localeCompare(timeB);
                    break;
                case 'days':
                    const daysA = [a.mon, a.tue, a.wed, a.thu, a.fri, a.sat, a.sun].join('');
                    const daysB = [b.mon, b.tue, b.wed, b.thu, b.fri, b.sat, b.sun].join('');
                    compareValue = daysA.localeCompare(daysB);
                    break;
                case 'room':
                    const roomA = `${a.stvbldgDesc || ''} ${a.roomCode || ''}`;
                    const roomB = `${b.stvbldgDesc || ''} ${b.roomCode || ''}`;
                    compareValue = roomA.localeCompare(roomB);
                    break;
                case 'capacity':
                    compareValue = (a.enrl || 0) - (b.enrl || 0);
                    break;
                case 'waitlist':
                    compareValue = (a.waitCount || 0) - (b.waitCount || 0);
                    break;
                case 'crosslist':
                    const crossA = a.crossListGroup || '';
                    const crossB = b.crossListGroup || '';
                    compareValue = crossA.localeCompare(crossB);
                    break;
                case 'instructor':
                    const instrA = a.pfaName || '';
                    const instrB = b.pfaName || '';
                    compareValue = instrA.localeCompare(instrB);
                    break;
                case 'ftyps':
                    const ftypsA = a.ftyps || '';
                    const ftypsB = b.ftyps || '';
                    compareValue = ftypsA.localeCompare(ftypsB);
                    break;
                case 'workload':
                    const workloadA = a.workload || '';
                    const workloadB = b.workload || '';
                    compareValue = workloadA.localeCompare(workloadB);
                    break;
                case 'duration':
                    const startA = a.ptrmStartDate || '';
                    const startB = b.ptrmStartDate || '';
                    compareValue = startA.localeCompare(startB);
                    break;
                case 'term':
                    const termA = a.ptrmDesc || '';
                    const termB = b.ptrmDesc || '';
                    compareValue = termA.localeCompare(termB);
                    break;
                default:
                    // Fall back to default sorting
                    const aHasTime = !!a.beginTime;
                    const bHasTime = !!b.beginTime;

                    if (aHasTime && !bHasTime) return -1;
                    if (!aHasTime && bHasTime) return 1;
                    if (!aHasTime && !bHasTime) return 0;

                    const aDate = a.ptrmStartDate;
                    const bDate = b.ptrmStartDate;

                    if (aDate && bDate) {
                        const dateComparison = aDate.localeCompare(bDate);
                        if (dateComparison !== 0) {
                            return dateComparison;
                        }
                    } else if (aDate) {
                        return -1;
                    } else if (bDate) {
                        return 1;
                    }

                    return a.beginTime.localeCompare(b.beginTime);
            }

            return sortOrder === 'asc' ? compareValue : -compareValue;
        } else {
            // Default sorting within same CRN
            const aHasTime = !!a.beginTime;
            const bHasTime = !!b.beginTime;

            if (aHasTime && !bHasTime) return -1;
            if (!aHasTime && bHasTime) return 1;
            if (!aHasTime && !bHasTime) return 0;

            const aDate = a.ptrmStartDate;
            const bDate = b.ptrmStartDate;

            if (aDate && bDate) {
                const dateComparison = aDate.localeCompare(bDate);
                if (dateComparison !== 0) {
                    return dateComparison;
                }
            } else if (aDate) {
                return -1;
            } else if (bDate) {
                return 1;
            }

            return a.beginTime.localeCompare(b.beginTime);
        }
    });

    // Recalculate isFirstRowForCRN and rowSpanForCRN after sorting
    const finalSections = [];
    let currentCRN = null;
    let crnCount = 0;
    let crnStartIndex = 0;

    sortedSections.forEach((sec, index) => {
        if (sec.crn !== currentCRN) {
            // Update rowSpanForCRN for previous CRN group
            if (currentCRN !== null) {
                for (let i = crnStartIndex; i < finalSections.length; i++) {
                    finalSections[i].rowSpanForCRN = crnCount;
                }
            }

            // Start new CRN group
            currentCRN = sec.crn;
            crnCount = 1;
            crnStartIndex = finalSections.length;

            finalSections.push({
                ...sec,
                isFirstRowForCRN: true
            });
        } else {
            crnCount++;
            finalSections.push({
                ...sec,
                isFirstRowForCRN: false
            });
        }
    });

    // Update rowSpanForCRN for the last CRN group
    if (currentCRN !== null) {
        for (let i = crnStartIndex; i < finalSections.length; i++) {
            finalSections[i].rowSpanForCRN = crnCount;
        }
    }

    const popoverLinkClass = "text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer hover:cursor-lancer capitalize";

    // Clipboard copy handler with feedback
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert(`Copied: ${text}`);
        });
    };
    if (renderTableBody) {
        // Render table rows only (for desktop table in SectionCards)
        return (
            <>
                {finalSections.map((sec, i) => (
                    <tr key={i} className={`border-t hover:bg-gray-50 transition-colors duration-150 text-sm text-gray-700 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                        {/* Status - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                                <div className="space-y-2">
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${getStatusConfig(sec.fwSectOpenInd).className}`}>
                                        {getStatusConfig(sec.fwSectOpenInd).label}
                                    </span>

                                    {(item.saprDesc || item.cohortDesc) && (
                                        <div className="inline-flex items-center space-x-1">
                                            <span className="bg-amber-100 text-amber-800 font-medium px-2 py-1 rounded-full">
                                                <div className="text-xs space-y-1">
                                                    {item.saprDesc && <div>{item.saprDesc}</div>}
                                                    {item.cohortDesc && <div><strong>Cohort:</strong> {item.cohortDesc}</div>}
                                                </div>
                                            </span>
                                        </div>
                                    )}
                                    {item?.schdCode === 'HY' ?
                                        <div>
                                            <Popover
                                                content={
                                                    <div className="text-xs space-y-1">
                                                        This section meets in-person on campus and has online work.
                                                    </div>
                                                }
                                                triggerClassName={popoverLinkClass}
                                            >
                                                <div className="inline-flex items-center space-x-1">
                                                    <span className="bg-teal-100 text-teal-800 font-medium px-2 py-1 rounded-full">
                                                        Hybrid
                                                    </span>
                                                </div>
                                            </Popover>
                                        </div> :
                                        <div></div>
                                    }
                                </div>
                            </td>
                        )}
                        {/* CRN - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                                <Popover
                                    content={
                                        <div className="p-2 text-white text-xs space-y-1">
                                            {[
                                                { label: "Date to Enroll", value: formatDateRange(sec.lastDateToEnroll) },
                                                { label: "Last day to add class", value: formatDateRange(sec.lastDateToEnroll) },
                                                { label: "Last day to drop with a refund", value: formatDateRange(sec.refundDate) },
                                                { label: "Census Date", value: formatDateRange(sec.censusDate) },
                                                { label: "Last day to declare grade mode", value: formatDateRange(sec.grademodeDate) },
                                                { label: "Last day to drop without a \"W\"", value: formatDateRange(sec.dropNoWDate) },
                                                { label: "Last day to drop with a \"W\"", value: formatDateRange(sec.dropWDate) },
                                            ].map((d, i) => (
                                                <div key={i} className="flex justify-between items-start">
                                                    <span className="mr-2 whitespace-nowrap">{d.label}:</span>
                                                    <span className="text-right">{d.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    description="Critical Dates for this Course"
                                    triggerClassName={popoverLinkClass}
                                >
                                    <span
                                        onClick={() => {
                                            setSelectedCRN(sec);
                                            setDrawerVisible(true);
                                        }}
                                    >
                                        {sec.crn}
                                    </span>
                                </Popover>
                            </td>
                        )}
                        {/* Units - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0 font-semibold text-center">{sec.creditHrLow}</td>
                        )}
                        {/* Type - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">{sec.schdCode || 'N/A'}</td>
                        )}
                        {/* Meet - individual for each row */}
                        <td className="py-3 px-3 border-r border-gray-100 last:border-r-0">{sec.ssrmeetSchdCode || 'N/A'}</td>
                        {/* Days - individual for each row */}
                        <td className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                            {/*Updates HA Assignments to only list TBA*/}
                            {sec.ssrmeetMtypCode?.trim().toUpperCase() === "HA"
                                ? "TBA"
                                : getDaysOfWeek(sec) || 'Online/TBA'}
                        </td>
                        {/* Time - individual for each row */}
                        <td className="py-3 px-3 border-r border-gray-100 last:border-r-0 font-medium">
                            {sec.beginTime && sec.endTime
                                ? `${formatTime(sec.beginTime)} - ${formatTime(sec.endTime)}`
                                /* Made changes to display specific hrs/wk text for different meeting types */
                                : ['TBA', 'HA'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                ? `${sec.weeklyHrs} Hrs/Wk In Addition To Any Scheduled Hours`
                                : ['WEB', 'BDE'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                    ? `${sec.weeklyHrs} Hrs/Wk Of Online Work`
                                    : `${sec.weeklyHrs} Hrs/Wk`}
                        </td>
                        {/* Room - individual for each row */}
                        <td className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                            {onlinePop.hasOwnProperty(sec.stvbldgDesc) ? (
                                <Popover
                                    content={<span className="text-xs">{onlinePop[sec.stvbldgDesc]}</span>}
                                    description={sec.stvbldgDesc || 'Online Info'}
                                    triggerClassName={popoverLinkClass}
                                >
                                    {sec.stvbldgDesc}
                                </Popover>
                            ) : (
                                <>
                                    {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : ` ${sec.roomCode}`}
                                </>
                            )}
                        </td>
                        {/* Capacity - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0 font-semibold text-center">{sec.enrl || 0}/{sec.maxEnrl || 0}</td>
                        )}
                        {/* Waitlist - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0 text-center">{sec.waitCount || 0}/{sec.waitCapacity || 0}</td>
                        )}
                        {/* Crosslist - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                                {sec.crossListGroup ? (sec.crossListGroup) : ''}
                            </td>
                        )}
                        {/* Instructor - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">
                                {instructorName.toLowerCase() === 'staff' ? (
                                    <span className="text-xs font-medium text-gray-800">Staff</span>
                                ) : (
                                    <span
                                        onClick={() => {
                                            setInstructorDrawerOpen(true);
                                            setInstructorData({
                                                primary: { name: instructorName, email: instructorEmail },
                                                secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                                    name: name.trim(),
                                                    email: secondaryInsturctorEmails?.[idx]?.trim()
                                                }))
                                            });
                                        }}
                                        className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer capitalize"
                                    >
                                        <div className="inline-flex items-center space-x-1">
                                            <span>{instructorName}{secondaryInsturctorNames?.length > 0 ? `, ${secondaryInsturctorNames.map(name => name.trim()).join(', ')}` : ''}</span>
                                        </div>
                                    </span>
                                )}
                            </td>
                        )}
                        {/* ftyps - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">{sec.ftyps}</td>
                        )}
                        {/* Workload - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">{sec.workload}</td>
                        )}
                        {/* Duration - individual for each row */}
                        <td className="py-3 px-3 border-r border-gray-100 last:border-r-0">{formatDateRangeDuration(sec.ptrmStartDate, sec.ptrmEndDate)}</td>
                        {/* Term - only show for first row of each CRN */}
                        {sec.isFirstRowForCRN && (
                            <td rowSpan={sec.rowSpanForCRN} className="py-3 px-3 border-r border-gray-100 last:border-r-0">{sec.ptrmDesc}</td>
                        )}
                    </tr >
                ))
                }
                {/* Section footnotes row */}
                {
                    (() => {
                        const uniqueFootnotes = [...new Set(finalSections
                            .map(sec => sec.sectionFootnotes)
                            .filter(f => f && f.trim()))];
                        return uniqueFootnotes.length > 0 ? (
                            <tr className="border-t border-gray-300 bg-yellow-50">
                                <td colSpan="16" className="py-3 px-4 text-xs text-gray-700">
                                    <div className="flex items-start">
                                        <div className="space-y-1">
                                            {uniqueFootnotes.map((footnote, i) => (
                                                <div key={i} className="block" dangerouslySetInnerHTML={createMarkup(footnote)}></div>
                                            ))}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ) : null;
                    })()
                }
                {
                    (() => {
                        const uniqueDescription = [...new Set(finalSections
                            .map(sec => sec.description)
                            .filter(f => f && f.trim()))];
                        return uniqueDescription.length > 0 ? (
                            <tr className="border-t border-gray-300 bg-yellow-50">
                                <td colSpan="16" className="py-3 px-4 text-xs text-gray-700">
                                    <div className="flex items-start">
                                        <div className="space-y-1">
                                            {uniqueDescription.map((description, i) => (
                                                <div key={i} className="block" dangerouslySetInnerHTML={createMarkup(description)}></div>
                                            ))}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        ) : null;
                    })()
                }
            </>
        );
    }

    // Render mobile cards (when renderTableBody is false)
    return (
        <div key={crn} className="px-4 py-4 border-t-2 border-pcc-cardinal bg-white rounded-lg mx-2 mb-4 shadow-sm">
            {/* CRN Header for mobile */}
            <div className="bg-gradient-to-r from-pcc-cardinal to-red-700 px-4 py-3 rounded-lg mb-4">
                <div className="flex items-center">
                    <span className="bg-white text-pcc-cardinal px-2 py-1 rounded-md text-xs font-bold mr-3">CRN</span>
                    <h4 className="text-sm font-bold text-white">{crn}</h4>
                </div>
            </div>
            <div className="w-full">
                <div className="flex flex-col gap-y-3">
                    {finalSections.map((sec, i) => (
                        <div key={i} className="flex flex-col gap-y-2 p-4 rounded-lg border border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-sm">
                            {/* Status */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                Status:
                                <span className={`ml-2 text-xs font-bold px-2 py-1 rounded-full ${getStatusConfig(item.fwSectOpenInd).className}`}>
                                    {getStatusConfig(item.fwSectOpenInd).label}
                                </span>
                            </div>

                            {/* CRN */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                CRN: <Popover
                                    content={
                                        <div className="p-2 text-white text-xs space-y-1">
                                            {[
                                                { label: "Date to Enroll", value: formatDateRange(item.lastDateToEnroll) },
                                                { label: "Last day to add class", value: formatDateRange(item.lastDateToEnroll) },
                                                { label: "Last day to drop with a refund", value: formatDateRange(item.refundDate) },
                                                { label: "Census Date", value: formatDateRange(item.censusDate) },
                                                { label: "Last day to declare grade mode", value: formatDateRange(item.grademodeDate) },
                                                { label: "Last day to drop without a \"W\"", value: formatDateRange(item.dropNoWDate) },
                                                { label: "Last day to drop with a \"W\"", value: formatDateRange(item.dropWDate) },
                                            ].map((d, i) => (
                                                <div key={i} className="flex justify-between items-start">
                                                    <span className="mr-2 whitespace-nowrap">{d.label}:</span>
                                                    <span className="text-right">{d.value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    description="Critical Dates for this Course"
                                    triggerClassName={popoverLinkClass}
                                >
                                    <span
                                        onClick={() => {
                                            setSelectedCRN(sec);
                                            setDrawerVisible(true);
                                        }}
                                        className="ml-2 text-blue-600 hover:underline cursor-pointer font-semibold"
                                    >
                                        {sec.crn}
                                    </span>
                                </Popover>
                            </div>

                            {/* Unit */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faBookOpen} className="mr-1.5 text-gray-500 w-3.5" />
                                Units: <span className="ml-2 font-semibold">{sec.creditHrLow}</span>
                            </div>

                            {/* Type */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                Type: {sec.schdCode || 'N/A'}
                            </div>

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faBookOpenReader} className="mr-1.5 text-gray-500 w-3.5" />
                                Format: {sec.ssrmeetSchdCode || 'N/A'}
                            </div>

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-500 w-3.5" />
                                Time: {sec.beginTime && sec.endTime
                                    ? `${formatTime(sec.beginTime)} - ${formatTime(sec.endTime)}`
                                    /* Made changes to display specific hrs/wk text for different meeting types */
                                    : ['TBA', 'HA'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                        ? `${sec.weeklyHrs} Hrs/Wk In Addition To Any Scheduled Hours`
                                        : ['WEB', 'BDE'].includes(sec.ssrmeetMtypCode?.trim().toUpperCase())
                                            ? `${sec.weeklyHrs} Hrs/Wk Of Online Work`
                                            : `${sec.weeklyHrs} Hrs/Wk`}
                            </div>

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                Day(s): {sec.ssrmeetMtypCode?.trim().toUpperCase() === "HA"
                                /* Updates HA Assignments to only list TBA for mobile view */
                                    ? "TBA"
                                    : getDaysOfWeek(sec) || 'Online/TBA'}
                            </div>

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                Room: {onlinePop.hasOwnProperty(sec.stvbldgDesc) ? (
                                    <Popover
                                        content={<span className="text-xs">{onlinePop[sec.stvbldgDesc]}</span>}
                                        description={sec.stvbldgDesc || 'Online Info'}
                                        triggerClassName={popoverLinkClass}
                                    >
                                        <span className="ml-2">{sec.stvbldgDesc}</span>
                                    </Popover>
                                ) : (
                                    <span className="ml-2">
                                        {sec.stvbldgDesc} {sec.roomCode === 'Needs Assignment' ? '' : `- ${sec.roomCode}`}
                                    </span>
                                )}
                            </div>

                            {/* Capacity */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faChair} className="mr-1.5 text-gray-500 w-3.5" />
                                Capacity: <span className="ml-2 font-semibold">{sec.enrl || 0}/{sec.maxEnrl || 0}</span>
                            </div>

                            {/* Waitlist */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faClockRotateLeft} className="mr-1.5 text-gray-500 w-3.5" />
                                Waitlist: <span className="ml-2">{sec.waitCount || 0}/{sec.waitCapacity || 0}</span>
                            </div>

                            {/* Crosslist */}
                            {sec.crossListGroup && (
                                <div className="flex items-center text-sm text-gray-700">
                                    <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                    Crosslist: <span className="ml-2">{sec.crossListGroup}</span>
                                </div>
                            )}

                            {/* Instructor */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faPersonChalkboard} className="mr-1.5 text-gray-500 w-3.5" />
                                Instructor: {instructorName.toLowerCase() === 'staff' ? (
                                    <span className="ml-2 text-xs font-medium text-gray-800">Staff</span>
                                ) : (
                                    <span
                                        onClick={() => {
                                            setInstructorDrawerOpen(true);
                                            setInstructorData({
                                                primary: { name: instructorName, email: instructorEmail },
                                                secondary: secondaryInsturctorNames?.map((name, idx) => ({
                                                    name: name.trim(),
                                                    email: secondaryInsturctorEmails?.[idx]?.trim()
                                                }))
                                            });
                                        }}
                                        className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer capitalize"
                                    >
                                        {instructorName}{secondaryInsturctorNames?.length > 0 ? `, ${secondaryInsturctorNames.map(name => name.trim()).join(', ')}` : ''}
                                    </span>
                                )}
                            </div>

                            {/* ftyps */}
                            {sec.ftyps && (
                                <div className="flex items-center text-sm text-gray-700">
                                    <FontAwesomeIcon icon={faTag} className="mr-1.5 text-gray-500 w-3.5" />
                                    ftyps: <span className="ml-2">{sec.ftyps}</span>
                                </div>
                            )}

                            {/* Workload */}
                            {sec.workload && (
                                <div className="flex items-center text-sm text-gray-700">
                                    <FontAwesomeIcon icon={faBookOpen} className="mr-1.5 text-gray-500 w-3.5" />
                                    Workload: <span className="ml-2">{sec.workload}</span>
                                </div>
                            )}

                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                Duration: <span className="ml-2">{formatDateRangeDuration(sec.ptrmStartDate, sec.ptrmEndDate)}</span>
                            </div>

                            {/* Term */}
                            <div className="flex items-center text-sm text-gray-700">
                                <FontAwesomeIcon icon={faCalendar} className="mr-1.5 text-gray-500 w-3.5" />
                                Term: <span className="ml-2">{sec.ptrmDesc}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
}

export default CRNColumns;